import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  Trophy,
  ChevronLeft,
  Save,
  Plus,
  Edit2,
  Copy,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  ListOrdered,
  Award,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Mock points schemas
const mockPointsSchemas = [
  {
    id: 1,
    schema_name: 'Standard Points - Feature',
    race_type: 'A Feature',
    is_default: true,
    positions: {
      1: 150, 2: 146, 3: 144, 4: 142, 5: 140,
      6: 138, 7: 136, 8: 134, 9: 132, 10: 130,
      11: 128, 12: 126, 13: 124, 14: 122, 15: 120,
      16: 118, 17: 116, 18: 114, 19: 112, 20: 110,
      21: 108, 22: 106, 23: 104, 24: 102, 25: 102,
      26: 102, 27: 102, 28: 102, 29: 102, 30: 102
    }
  },
  {
    id: 2,
    schema_name: 'Heat Race Points',
    race_type: 'Heat',
    is_default: true,
    positions: {
      1: 10, 2: 8, 3: 6, 4: 5, 5: 4,
      6: 3, 7: 2, 8: 1, 9: 0, 10: 0
    }
  },
  {
    id: 3,
    schema_name: 'Qualifying Points',
    race_type: 'Qualifying',
    is_default: true,
    positions: {
      1: 4, 2: 3, 3: 2, 4: 1, 5: 0
    }
  },
  {
    id: 4,
    schema_name: 'Last Chance Showdown',
    race_type: 'B Feature',
    is_default: true,
    positions: {
      1: 50, 2: 48, 3: 46, 4: 44, 5: 42,
      6: 40, 7: 38, 8: 36, 9: 34, 10: 32,
      11: 30, 12: 28, 13: 26, 14: 24, 15: 22
    }
  }
];

const raceTypes = [
  { id: 'a-feature', name: 'A Feature', category: 'Feature' },
  { id: 'b-feature', name: 'B Feature (LCS)', category: 'Feature' },
  { id: 'c-feature', name: 'C Feature', category: 'Feature' },
  { id: 'heat', name: 'Heat Race', category: 'Preliminary' },
  { id: 'qualifying', name: 'Qualifying', category: 'Preliminary' },
  { id: 'dash', name: 'Dash', category: 'Special' },
  { id: 'hot-laps', name: 'Hot Laps', category: 'Practice' }
];

const tiebreakerOptions = [
  { id: 'wins', name: 'Most Wins', description: 'Driver with more wins ranks higher' },
  { id: 'top5', name: 'Most Top 5s', description: 'Driver with more top 5 finishes ranks higher' },
  { id: 'top10', name: 'Most Top 10s', description: 'Driver with more top 10 finishes ranks higher' },
  { id: 'best-finish', name: 'Best Finish', description: 'Driver with better best finish ranks higher' },
  { id: 'recent', name: 'Most Recent Win', description: 'Driver with more recent win ranks higher' }
];

function PointsSchemaCard({ schema, isExpanded, onToggle, onEdit, onDelete, onDuplicate }) {
  const positionCount = Object.keys(schema.positions).length;
  const maxPoints = Math.max(...Object.values(schema.positions));

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            schema.race_type.includes('Feature') ? 'bg-amber-100' :
            schema.race_type.includes('Heat') ? 'bg-blue-100' :
            'bg-slate-100'
          }`}>
            <Trophy className={`w-5 h-5 ${
              schema.race_type.includes('Feature') ? 'text-amber-600' :
              schema.race_type.includes('Heat') ? 'text-blue-600' :
              'text-slate-600'
            }`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">{schema.schema_name}</h3>
              {schema.is_default && (
                <Badge variant="secondary" size="sm">Default</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">
              {schema.race_type} • {positionCount} positions • Max {maxPoints} pts
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(schema);
            }}
            title="Duplicate schema"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(schema);
            }}
            title="Edit schema"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          {!schema.is_default && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(schema);
              }}
              title="Delete schema"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Content - Points Table */}
      {isExpanded && (
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
            {Object.entries(schema.positions).map(([position, points]) => (
              <div
                key={position}
                className="p-2 rounded-lg bg-white border border-slate-200 text-center"
              >
                <div className="text-xs text-slate-500 mb-1">P{position}</div>
                <div className="font-semibold text-slate-800">{points}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SchemaEditModal({ schema, raceTypes, onSave, onClose }) {
  const isNew = !schema?.id;
  const initialPositionCount = Object.keys(schema?.positions || {}).length || 30;
  const [formData, setFormData] = useState(() => {
    const positions = schema?.positions || {};
    if (Object.keys(positions).length === 0) {
      const defaultPositions = {};
      for (let i = 1; i <= initialPositionCount; i++) {
        defaultPositions[i] = Math.max(0, 150 - (i - 1) * 4);
      }
      return {
        schema_name: schema?.schema_name || '',
        race_type: schema?.race_type || 'A Feature',
        is_default: schema?.is_default || false,
        positions: defaultPositions
      };
    }
    return {
      schema_name: schema?.schema_name || '',
      race_type: schema?.race_type || 'A Feature',
      is_default: schema?.is_default || false,
      positions
    };
  });
  const [positionCount, setPositionCount] = useState(initialPositionCount);
  const [saving, setSaving] = useState(false);

  const handlePositionChange = (position, value) => {
    setFormData(prev => ({
      ...prev,
      positions: {
        ...prev.positions,
        [position]: parseInt(value) || 0
      }
    }));
  };

  const handlePositionCountChange = (newCount) => {
    const count = parseInt(newCount) || 1;
    setPositionCount(count);

    const newPositions = {};
    for (let i = 1; i <= count; i++) {
      newPositions[i] = formData.positions[i] !== undefined
        ? formData.positions[i]
        : Math.max(0, 150 - (i - 1) * 4);
    }
    setFormData(prev => ({ ...prev, positions: newPositions }));
  };

  const applyFormula = (type) => {
    const newPositions = {};
    const count = positionCount;

    switch (type) {
      case 'linear':
        // Linear decrease: 150, 146, 142, 138...
        for (let i = 1; i <= count; i++) {
          newPositions[i] = Math.max(0, 150 - (i - 1) * 4);
        }
        break;
      case 'winner-heavy':
        // More points for top positions
        for (let i = 1; i <= count; i++) {
          if (i === 1) newPositions[i] = 175;
          else if (i === 2) newPositions[i] = 160;
          else if (i === 3) newPositions[i] = 150;
          else newPositions[i] = Math.max(0, 140 - (i - 4) * 3);
        }
        break;
      case 'flat':
        // Flat points for all positions
        for (let i = 1; i <= count; i++) {
          newPositions[i] = 100;
        }
        break;
      case 'heat':
        // Heat race style: small points
        for (let i = 1; i <= count; i++) {
          newPositions[i] = Math.max(0, 10 - (i - 1));
        }
        break;
      default:
        break;
    }

    setFormData(prev => ({ ...prev, positions: newPositions }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onSave({
      ...schema,
      ...formData,
      id: schema?.id || Date.now()
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-8">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {isNew ? 'Create Points Schema' : 'Edit Points Schema'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Schema Name"
              value={formData.schema_name}
              onChange={(e) => setFormData({ ...formData, schema_name: e.target.value })}
              placeholder="e.g., Standard Points - Feature"
              required
            />
            <Select
              label="Race Type"
              value={formData.race_type}
              onChange={(e) => setFormData({ ...formData, race_type: e.target.value })}
            >
              <option value="A Feature">A Feature</option>
              <option value="B Feature">B Feature (LCS)</option>
              <option value="C Feature">C Feature</option>
              <option value="Heat">Heat Race</option>
              <option value="Qualifying">Qualifying</option>
              <option value="Dash">Dash</option>
            </Select>
          </div>

          {/* Position Count & Quick Apply */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-32">
              <Input
                label="Positions"
                type="number"
                min={1}
                max={50}
                value={positionCount}
                onChange={(e) => handlePositionCountChange(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Quick Apply Formula
              </label>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => applyFormula('linear')}>
                  Linear
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => applyFormula('winner-heavy')}>
                  Winner Heavy
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => applyFormula('flat')}>
                  Flat
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => applyFormula('heat')}>
                  Heat Style
                </Button>
              </div>
            </div>
          </div>

          {/* Points Grid */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Points by Position
            </label>
            <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-lg border border-slate-200">
              {Object.entries(formData.positions).map(([position, points]) => (
                <div key={position} className="relative">
                  <label className="absolute -top-1 left-1 text-[10px] text-slate-400 bg-white px-1">
                    P{position}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={points}
                    onChange={(e) => handlePositionChange(position, e.target.value)}
                    className="w-full px-2 py-2 pt-3 text-center text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rms-accent/50 focus:border-rms-accent"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Default Checkbox */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <input
              type="checkbox"
              id="is_default"
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-rms-accent focus:ring-rms-accent"
            />
            <label htmlFor="is_default" className="text-sm text-slate-700">
              Set as default schema for this race type
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isNew ? 'Create Schema' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PointsConfig() {
  const { seriesSlug } = useParams();
  const currentSeries = useSeries();

  const [schemas, setSchemas] = useState([]);
  const [expandedSchemas, setExpandedSchemas] = useState(new Set());
  const [editingSchema, setEditingSchema] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Tiebreaker settings
  const [tiebreakers, setTiebreakers] = useState(['wins', 'top5', 'top10']);
  const [dropWeeks, setDropWeeks] = useState(0);

  const { isLoading: loading } = useQuery({
    queryKey: ['points-schemas'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      setSchemas(mockPointsSchemas);
      if (mockPointsSchemas.length > 0) {
        setExpandedSchemas(new Set([mockPointsSchemas[0].id]));
      }
      return mockPointsSchemas;
    },
  });

  const toggleExpand = (schemaId) => {
    setExpandedSchemas(prev => {
      const next = new Set(prev);
      if (next.has(schemaId)) {
        next.delete(schemaId);
      } else {
        next.add(schemaId);
      }
      return next;
    });
  };

  const handleSaveSchema = (savedSchema) => {
    if (editingSchema) {
      setSchemas(schemas.map(s => s.id === savedSchema.id ? savedSchema : s));
    } else {
      setSchemas([...schemas, savedSchema]);
      setExpandedSchemas(prev => new Set([...prev, savedSchema.id]));
    }
    setEditingSchema(null);
    setShowCreateModal(false);
  };

  const handleDeleteSchema = (schema) => {
    if (window.confirm(`Delete "${schema.schema_name}"? This action cannot be undone.`)) {
      setSchemas(schemas.filter(s => s.id !== schema.id));
    }
  };

  const handleDuplicateSchema = (schema) => {
    const duplicate = {
      ...schema,
      id: Date.now(),
      schema_name: `${schema.schema_name} (Copy)`,
      is_default: false
    };
    setSchemas([...schemas, duplicate]);
    setExpandedSchemas(prev => new Set([...prev, duplicate.id]));
  };

  const moveTiebreaker = (index, direction) => {
    const newTiebreakers = [...tiebreakers];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < tiebreakers.length) {
      [newTiebreakers[index], newTiebreakers[newIndex]] = [newTiebreakers[newIndex], newTiebreakers[index]];
      setTiebreakers(newTiebreakers);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Points Configuration</h1>
            <p className="text-slate-500 mt-1">
              Configure point structures for {currentSeries?.name || 'series'}
            </p>
          </div>
          <Button leftIcon={Plus} onClick={() => setShowCreateModal(true)}>
            New Schema
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Points Schemas */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Points Schemas</CardTitle>
              <CardDescription>
                Define how points are awarded for each race type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : schemas.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No points schemas configured</p>
                  <Button
                    variant="secondary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create First Schema
                  </Button>
                </div>
              ) : (
                schemas.map((schema) => (
                  <PointsSchemaCard
                    key={schema.id}
                    schema={schema}
                    isExpanded={expandedSchemas.has(schema.id)}
                    onToggle={() => toggleExpand(schema.id)}
                    onEdit={setEditingSchema}
                    onDelete={handleDeleteSchema}
                    onDuplicate={handleDuplicateSchema}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Settings Sidebar */}
        <div className="space-y-6">
          {/* Tiebreaker Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ListOrdered className="w-5 h-5" />
                Tiebreaker Rules
              </CardTitle>
              <CardDescription>
                Order of tiebreakers when points are equal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tiebreakers.map((tb, index) => {
                  const option = tiebreakerOptions.find(o => o.id === tb);
                  return (
                    <div
                      key={tb}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
                    >
                      <span className="w-6 h-6 rounded-full bg-rms-accent/10 text-rms-accent text-sm font-medium flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{option?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{option?.description}</p>
                      </div>
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveTiebreaker(index, -1)}
                          disabled={index === 0}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4 rotate-180" />
                        </button>
                        <button
                          onClick={() => moveTiebreaker(index, 1)}
                          disabled={index === tiebreakers.length - 1}
                          className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Drop Weeks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Drop Weeks
              </CardTitle>
              <CardDescription>
                Number of worst finishes to drop from standings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={dropWeeks}
                  onChange={(e) => setDropWeeks(parseInt(e.target.value) || 0)}
                  className="w-24"
                />
                <span className="text-sm text-slate-500">
                  {dropWeeks === 0 ? 'No drops' : `${dropWeeks} worst ${dropWeeks === 1 ? 'finish' : 'finishes'} dropped`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Bonus Points */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Bonus Points
              </CardTitle>
              <CardDescription>
                Additional points awarded for achievements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm text-slate-700">Pole Position</span>
                  <Input type="number" min={0} defaultValue={0} className="w-20 text-center" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm text-slate-700">Most Laps Led</span>
                  <Input type="number" min={0} defaultValue={0} className="w-20 text-center" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm text-slate-700">Hard Charger</span>
                  <Input type="number" min={0} defaultValue={0} className="w-20 text-center" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save All Button */}
          <Button className="w-full" leftIcon={Save}>
            Save All Settings
          </Button>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {(editingSchema || showCreateModal) && (
        <SchemaEditModal
          schema={editingSchema}
          raceTypes={raceTypes}
          onSave={handleSaveSchema}
          onClose={() => {
            setEditingSchema(null);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}
